import { useTranslate } from '../../lib/i18n';
import { formatMessageTime } from '../../utils/timeFormatters';
import MarkdownRenderer from '../MarkdownRenderer';
import RoboticIcon from '../customIcons/RoboticIcon';

type StructuredCoachFeedback = {
  kokonaisuus: {
    nayta: boolean;
    vahvuudet: string | null;
    huomioitavaa: string | null;
  };
  viesti: {
    nayta: boolean;
    vahvuudet: string | null;
    kehitysehdotus: string | null;
  };
  positiveMessage: string | null;
};

interface Props {
  id: string;
  content: string;
  time: string | number;
  isInSidebar?: boolean;
}

interface FeedbackFieldProps {
  content: string;
}

const FeedbackField = ({ content }: FeedbackFieldProps) => (
  <div className="prose-sm prose">
    <MarkdownRenderer markdown={content} />
  </div>
);

interface StructuredFeedbackProps {
  feedback: StructuredCoachFeedback;
  t: ReturnType<typeof useTranslate>;
}

const StructuredFeedback = ({ feedback, t }: StructuredFeedbackProps) => {
  const { kokonaisuus, viesti, positiveMessage } = feedback;

  if (!kokonaisuus.nayta && !viesti.nayta) {
    return positiveMessage ? <div>{positiveMessage}</div> : null;
  }

  return (
    <div className="space-y-4">
      {kokonaisuus.nayta && (
        <div className="space-y-2">
          {kokonaisuus.vahvuudet && (
            <div>
              <span className="font-medium text-xs text-muted-foreground">
                {t('operatorChatbot.feedback.strengths')}
              </span>
              <FeedbackField content={kokonaisuus.vahvuudet} />
            </div>
          )}
          {kokonaisuus.huomioitavaa && (
            <div>
              <span className="font-medium text-xs text-muted-foreground">
                {t('operatorChatbot.feedback.considerations')}
              </span>
              <FeedbackField content={kokonaisuus.huomioitavaa} />
            </div>
          )}
        </div>
      )}
      {viesti.nayta && (
        <div className="space-y-2">
          {viesti.vahvuudet && (
            <div>
              <span className="font-medium text-xs text-muted-foreground">
                {t('operatorChatbot.feedback.strengths')}
              </span>
              <FeedbackField content={viesti.vahvuudet} />
            </div>
          )}
          {viesti.kehitysehdotus && (
            <div>
              <span className="font-medium text-xs text-muted-foreground">
                {t('operatorChatbot.feedback.suggestion')}
              </span>
              <FeedbackField content={viesti.kehitysehdotus} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CoachMessage = ({ id, content, time, isInSidebar = false }: Props) => {
  const formattedTime = formatMessageTime(time);
  const t = useTranslate();

  let structuredFeedback: StructuredCoachFeedback | null = null;
  try {
    structuredFeedback = JSON.parse(content);
  } catch {
    // Invalid JSON — will render as markdown
  }

  return (
    <div
      data-testid="coach-feedback"
      id={id}
      className={`relative mb-3 ${isInSidebar ? 'py-2' : 'rounded-lg bg-tertiary/10 p-4'}`}
    >
      {!isInSidebar && (
        <div className="flex flex-col items-center text-sm mb-3">
          <div className="mb-1">
            <RoboticIcon size={70} />
          </div>
          <div className="flex flex-col items-center">
            <span className="font-medium text-center">{t('operatorChatbot.coach')}</span>
            {formattedTime && (
              <span className="text-[10px] text-muted-foreground mt-1">{formattedTime}</span>
            )}
          </div>
        </div>
      )}
      <div className={`prose-sm marker:text-foreground prose ${isInSidebar ? 'text-sm' : ''}`}>
        {structuredFeedback ? (
          <StructuredFeedback feedback={structuredFeedback} t={t} />
        ) : (
          <MarkdownRenderer markdown={content} />
        )}
      </div>
      {isInSidebar && formattedTime && (
        <div className="text-right text-[10px] text-muted-foreground mt-2 pr-1">
          {formattedTime}
        </div>
      )}
    </div>
  );
};

export default CoachMessage;
